import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { Box, Container, Grid, makeStyles } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import {
  useWizardCurrentStepConfiguration,
  useWizardGoToNextStep,
  useWizardGoToPreviousStep,
  useWizardStepsDone,
  useWizardTotalSteps,
} from '../../contexts/ClientSideWizard/hooks';
import { WizardStepperTypeEnum } from '../../contexts/ClientSideWizard/types';
import { ClientSideWizardStepper } from './ClientSideWizardStepper';
import { FormStep } from './Steps';

const useStyles = makeStyles(theme => ({
  backButton: {
    position: 'absolute',
    left: 0,
    top: theme.spacing(10),
    transform: 'translateX(-50%)',
    [theme.breakpoints.down('sm')]: {
      transform: 'translateX(0)',
      position: 'fixed',
      left: theme.spacing(2),
      top: theme.spacing(2),
    },
  },
  flex: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
  },
  paddingBottom: {
    paddingBottom: (props: any) => (Boolean(props.hasStepIndicator) ? theme.spacing(6) : theme.spacing(3)),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: '0 !important',
    },
  },
  isTakeover: {
    height: '100%',
  },
  checkbox: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(-1),

    '& + span': {
      color: theme.palette.secondary.light,
      width: '60%',
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        width: '80%',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  },
  hasOverflowYAuto: {
    overflowY: 'auto',
  },
  dynamicComponent: {
    marginRight: (props: any) => (props?.isMobile ? 0 : theme.spacing(2)),
    marginBottom: (props: any) => (props?.isMobile ? theme.spacing(2) : 0),
  },
}));

export type ClientSideWizardProps = {
  onSubmit?: Function;
  onCancel?: React.MouseEventHandler;
  isTakeover?: Boolean;
  stepperType?: WizardStepperTypeEnum;
  stepperConfig?: GenericObject[];
  hasProgressbar?: boolean;
  hasOverflowYAuto?: Boolean;
  externalEffectCallbacks?: GenericObject;
  shouldEnablePaddingBottom?: boolean;
  rootContainerClasses?: string;
  childContainerClasses?: string;
};

const ClientSideWizard: FC<ClientSideWizardProps> = ({
  onSubmit,
  onCancel,
  isTakeover,
  stepperType,
  stepperConfig,
  hasProgressbar,
  hasOverflowYAuto,
  externalEffectCallbacks,
  shouldEnablePaddingBottom = true,
  rootContainerClasses,
  childContainerClasses,
}) => {
  const { t } = useTranslation();
  const stepsDone = useWizardStepsDone();
  const totalSteps = useWizardTotalSteps();
  const goToNextWizardStep = useWizardGoToNextStep();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const currentStepConfig = useWizardCurrentStepConfiguration();
  const goToPreviousWizardStep = useWizardGoToPreviousStep();

  let { Component, parameters, cancelHidden, backable, hasStepIndicator } = currentStepConfig || {};

  const classes = useStyles({ hasStepIndicator, isMobile });

  if (!isDefined(Component)) {
    Component = FormStep;
  }

  const shouldShowWizardStepper = hasStepIndicator && !!stepperConfig && !!stepperType && !isMobile;

  return !currentStepConfig ? null : (
    <>
      <Grid
        container
        item
        justifyContent={isMobile ? 'flex-start' : 'center'}
        alignItems='center'
        alignContent='center'
        wrap={'nowrap'}
        direction='column'
        className={clsx(classes.flex, rootContainerClasses, {
          [classes.isTakeover]: Boolean(isTakeover),
          [classes.paddingBottom]: shouldEnablePaddingBottom,
        })}>
        {shouldShowWizardStepper && (
          <Box position={'fixed'} top={'140px'} px={10}>
            <ClientSideWizardStepper
              type={stepperType}
              stepperConfig={stepperConfig}
              totalSteps={totalSteps}
              totalStepsCompleted={stepsDone}
              ariaLabel={t('clientSideWizard.ClientSideWizardStepper.ariaLabel', {
                done: `${Math.max(0, stepsDone)}`,
                total: `${totalSteps}`,
              })}
            />
            {hasProgressbar && <span>TODO: progress bar here</span>}
          </Box>
        )}
        <Container
          maxWidth={parameters?.maxWidth || false}
          disableGutters={parameters?.disableGutters}
          fixed={parameters?.fixed}
          className={clsx(classes.flex, childContainerClasses, {
            [classes.isTakeover]: Boolean(isTakeover),
            [classes.hasOverflowYAuto]: Boolean(hasOverflowYAuto),
            [classes.paddingBottom]: shouldEnablePaddingBottom,
          })}>
          <Component
            title={currentStepConfig.title}
            externalEffectCallbacks={{
              onCancel,
              onSubmit,
              onBack: goToPreviousWizardStep,
              onNext: goToNextWizardStep,
              ...externalEffectCallbacks,
            }}
            externalProps={{
              backable,
              cancelHidden,
              lastStep: stepsDone === totalSteps - 1,
            }}
            currentStep={stepsDone}
            contentsConfig={parameters}
            classes={{
              checkbox: classes.checkbox,
              dynamicComponent: classes.dynamicComponent,
            }}
          />
        </Container>
      </Grid>

      {/* TODO: Wizard should support conditional back button */}

      {/* TODO: Wizard should support conditional cancel button */}
    </>
  );
};

export { ClientSideWizard };
